import React, { useEffect, useCallback } from 'react'

import PropTypes from 'prop-types'

import { useDispatch, useSelector } from 'react-redux'
import { selectAuth, selectHash, selectCurrentLocation, selectElectronOSUser, selectIsElectron } from '@selectors'
import { HIDE_ACTION_SHEET, LOCATION_FETCHING, LOCATION_RESET, SET_CONFIRMATION_MODAL } from '@redux/actions/types'

import { panicButtonAction, priorityConnectAction } from '@redux/actions/panicbutton'

import { ActionSheet, RED, RED_70, WHITE } from '@omnilert/components'

import useStyles from './emergency-action-sheet.style'

import i18n from 'i18n-js'
import { BUTTON_CANCEL, BUTTON_DIAL_911, BUTTON_DIAL_911_SHORT, BUTTON_SUBMIT_SILENT_ALERT, MESSAGE_LOCATION_ERROR_TOAST, MODAL_BODY_TIP_EMERGENCY } from '../../assets/i18n/strings'

const EmergencyActionSheet = props => {
  const isVisible = useSelector((state) => state.sheet.actionSheet === 'EMERGENCY')

  const classes = useStyles({ ...props, isVisible })

  const dispatch = useDispatch()

  const Analytics = useSelector(state => state.analytic.analytics)

  const pac = useSelector(selectHash)

  const auth = useSelector(selectAuth)

  const location = useSelector(selectCurrentLocation)
  const currentOSUser = useSelector(selectElectronOSUser)

  const { accountConfig } = auth

  useEffect(() => {
    if (isVisible) {
      dispatch({ type: LOCATION_FETCHING })
    } else {
      dispatch({ type: LOCATION_RESET })
    }
  }, [dispatch, isVisible])

  const panicButton = accountConfig?.panic_button

  const panicButtonNumber = panicButton?.panic_button_number

  const enablePanicButtonReminder = panicButton?.enable_panic_button_reminder === '1'

  const enableSilentAlarm = panicButton?.silent_alarm

  const panicButtonTitle = panicButton?.panic_button_title

  const enablePriorityConnect = true
  const isElectron = useSelector(selectIsElectron)

  const EMERGENCY_DATA = () => {
    let silentOption = null

    if (enableSilentAlarm) {
      silentOption = {
        title: i18n.t(BUTTON_SUBMIT_SILENT_ALERT),
        number: null,
        type: 'silent',
        icon: 'alert'
      }
    }

    let customOption = null

    if (panicButtonTitle && panicButtonNumber) {
      customOption = {
        title: panicButtonTitle,
        number: `tel:${panicButtonNumber}`,
        type: 'custom',
        icon: 'phone'
      }
    }

    let emergencyOption = null

    if (enablePanicButtonReminder) {
      emergencyOption = {
        title: i18n.t(BUTTON_DIAL_911),
        number: 'tel:911',
        type: 'emergency',
        icon: 'phone'
      }
    }

    let priorityConnectOption = null

    if (enablePriorityConnect) {
      priorityConnectOption = {
        title: 'Priority Connect',
        number: null,
        type: 'priority',
        icon: 'phone'
      }
    }

    const cancelOption = {
      title: i18n.t(BUTTON_CANCEL),
      number: null,
      type: 'cancel',
      icon: null
    }

    return [
      customOption,
      emergencyOption,
      silentOption,
      priorityConnectOption,
      cancelOption
    ].filter(elem => elem !== null)
  }

  const onClick = useCallback(({ number, type }) => {
    let bodyText = [{
      color: WHITE,
      size: 'lg',
      text: i18n.t(MODAL_BODY_TIP_EMERGENCY)
    }]

    if (type === 'priority') {
      bodyText = [
        {
          color: WHITE,
          size: 'lg',
          text: 'Your urgent help request and information will be sent to your organization.'
        }, {
          color: WHITE,
          size: 'lg',
          text: 'You will be connected to security personel immediately.'
        }
      ]
    }

    const payload = {
      action: type,
      location: null,
      personal_access_code: pac,
      current_os_user: currentOSUser
    }

    if (location) {
      payload.location = {
        lat: location.latitude,
        lng: location.longitude
      }
    } else {
      bodyText = [
        {
          color: 'orange',
          size: 'lg',
          text: i18n.t(MESSAGE_LOCATION_ERROR_TOAST)
        },
        ...bodyText
      ]
    }

    const handleOnDismiss = () => {
      dispatch({
        type: SET_CONFIRMATION_MODAL,
        payload: {
          isVisible: false
        }
      })
    }

    const handleOnConfirm = () => {
      if (number) {
        window.location.href = number
      }
      dispatch(panicButtonAction({ auth, data: payload }))

      handleOnDismiss()
    }

    const handlePriorityConnect = () => {
      dispatch(priorityConnectAction({ auth, data: payload }))

      handleOnDismiss()
    }

    switch (type) {
    case 'silent':
      Analytics.logEvent('PanicButtonAlertSilent')
      dispatch({
        type: SET_CONFIRMATION_MODAL,
        payload: {
          isCancellable: true,
          confirmButtonBackgroundColor: RED,
          confirmButtonHoverBackgroundColor: RED_70,
          confirmText: i18n.t(BUTTON_SUBMIT_SILENT_ALERT),
          onConfirm: handleOnConfirm,
          onDismiss: handleOnDismiss,
          iconName: 'alert',
          iconVariant: 'error',
          isVisible: true,
          bodyText,
          title: ''
        }
      })
      break
    case 'emergency':
      dispatch({
        type: SET_CONFIRMATION_MODAL,
        payload: {
          isCancellable: true,
          confirmButtonBackgroundColor: RED,
          confirmButtonHoverBackgroundColor: RED_70,
          confirmText: i18n.t(BUTTON_DIAL_911_SHORT),
          onConfirm: handleOnConfirm,
          onDismiss: handleOnDismiss,
          iconName: 'alert',
          iconVariant: 'error',
          isVisible: true,
          bodyText,
          title: ''
        }
      })
      Analytics.logEvent('PanicButtonAlert911')
      break
    case 'custom':
      dispatch({
        type: SET_CONFIRMATION_MODAL,
        payload: {
          isCancellable: true,
          confirmButtonBackgroundColor: RED,
          confirmButtonHoverBackgroundColor: RED_70,
          confirmText: `Dial ${panicButtonTitle}`,
          onConfirm: handleOnConfirm,
          onDismiss: handleOnDismiss,
          iconName: 'alert',
          iconVariant: 'error',
          isVisible: true,
          bodyText,
          title: ''
        }
      })
      Analytics.logEvent('PanicButtonAlertCustom')
      break
    case 'priority':
      dispatch({
        type: SET_CONFIRMATION_MODAL,
        payload: {
          isCancellable: true,
          confirmButtonBackgroundColor: RED,
          confirmButtonHoverBackgroundColor: RED_70,
          confirmText: 'Connect to security',
          onConfirm: handlePriorityConnect,
          onDismiss: handleOnDismiss,
          iconName: 'alert',
          iconVariant: 'error',
          isVisible: true,
          bodyText,
          title: ''
        }

      })
      break
    case 'cancel':
      Analytics.logEvent('PanicButtonAlertCancel')
      break
    default:
      break
    }

    dispatch({ type: HIDE_ACTION_SHEET })
  }, [
    Analytics,
    auth,
    dispatch,
    location,
    pac,
    panicButtonTitle,
    currentOSUser
  ])
  
  const handleOnCloseActionSheet = () => dispatch({ type: HIDE_ACTION_SHEET })

  useEffect(() => {
    if (isElectron) {
      window.api.receive('silent:alert:post', () => onClick({
        title: i18n.t(BUTTON_SUBMIT_SILENT_ALERT),
        number: null,
        type: 'silent',
        icon: 'alert'
      }))
    }
  }, [isElectron, onClick])

  return (
    <ActionSheet
      onClose={handleOnCloseActionSheet}
      show={isVisible}
    >
      <ul className={classes.list}>
        {EMERGENCY_DATA().map((data, index) => (
          <li
            key={index}
            className={classes.actionItem}
            onClick={e => onClick(data)}
          >
            <div className={`${classes.textContainer} ${data.type}`}>
              <div>{data.title}</div>
            </div>
          </li>
        ))}
      </ul>
    </ActionSheet>
  )
}

EmergencyActionSheet.defaultProps = {
  isVisible: false
}

EmergencyActionSheet.propTypes = {
  isVisible: PropTypes.bool
}

export default EmergencyActionSheet
